import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VisuallyHidden,
  VStack,
} from "@chakra-ui/react";
import {
  FiAlertTriangle,
  FiCalendar,
  FiExternalLink,
  FiLock,
  FiX,
} from "react-icons/fi";
import dayjs from "dayjs";

type EtaAlertType = "soft" | "hard";
/** modal = Soft/Hard dialog; locked-details = reminder banner while PE/OH work */
type EtaAlertPresentation = "modal" | "locked-details";

type Props = {
  isOpen: boolean;
  alertType: EtaAlertType;
  presentation?: EtaAlertPresentation;
  orderNo?: string;
  eta?: string;
  onDismiss?: () => void;
  onExtendEta: () => void;
  onViewDetails?: () => void;
  onBackToLock?: () => void;
  isExtendLoading?: boolean;
};

const ETA_ALERT_TITLE_ID = "eta-alert-modal-title";
const ETA_ALERT_BANNER_TITLE_ID = "eta-alert-banner-title";

const THEME = {
  soft: {
    accent: "#E67E22",
    accentSoft: "#FDF0E6",
    title: "ETA Approaching",
    titleColor: "gray.800",
    body: (orderNo?: string) =>
      orderNo
        ? `The ETA for #${orderNo} is approaching.`
        : "The ETA for this enquiry/order is approaching.",
    hint: "Please review and extend the ETA if required to keep the customer informed.",
    footer: "You can continue working.",
    showDismiss: true,
    closeOnOverlay: true,
  },
  hard: {
    accent: "#E53E3E",
    accentSoft: "#FDEAEA",
    title: "ETA Overdue",
    titleColor: "#E53E3E",
    body: (orderNo?: string) =>
      orderNo
        ? `The ETA for #${orderNo} has already passed.`
        : "The ETA for this enquiry/order has already passed.",
    hint: "You can work on this enquiry/order only. Extend ETA to unlock other work.",
    footer: "Work only on this enquiry/order until ETA is extended.",
    showDismiss: false,
    closeOnOverlay: false,
  },
} as const;

function formatEtaDisplay(eta?: string): string {
  if (!eta) return "—";
  const parsed = dayjs(eta);
  if (!parsed.isValid()) return eta;
  return parsed.format("DD MMM YYYY, hh:mm A");
}

const EtaAlertModal = ({
  isOpen,
  alertType,
  presentation = "modal",
  orderNo,
  eta,
  onDismiss,
  onExtendEta,
  onViewDetails,
  onBackToLock,
  isExtendLoading = false,
}: Props) => {
  const theme = THEME[alertType];
  const canViewDetails = Boolean(orderNo && onViewDetails);

  if (!isOpen) {
    return null;
  }

  // Reminder banner — page below stays interactive; do not mark as modal
  if (presentation === "locked-details") {
    return (
      <Box
        data-eta-extend-allowed
        role="alert"
        aria-labelledby={ETA_ALERT_BANNER_TITLE_ID}
        aria-live="assertive"
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        zIndex="popover"
        bg="white"
        borderTopWidth="3px"
        borderTopColor={theme.accent}
        boxShadow="0 -8px 24px rgba(0,0,0,0.22)"
        px={{ base: 3, md: 6 }}
        py={3}
        pointerEvents="auto"
      >
        <Flex
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          gap={3}
          direction={{ base: "column", md: "row" }}
          maxW="1200px"
          mx="auto"
        >
          <HStack spacing={3} align="flex-start" flex={1}>
            <Flex
              align="center"
              justify="center"
              w="36px"
              h="36px"
              borderRadius="full"
              bg={theme.accent}
              color="white"
              flexShrink={0}
            >
              <Icon as={FiLock} boxSize={4} />
            </Flex>
            <Box>
              <Text
                id={ETA_ALERT_BANNER_TITLE_ID}
                fontWeight="bold"
                color={theme.titleColor}
                fontSize="md"
              >
                {theme.title}
                {orderNo ? ` · #${orderNo}` : ""}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Work only on this enquiry/order. Extend ETA to unlock
                everything else.
                {eta ? ` ETA: ${formatEtaDisplay(eta)}` : ""}
              </Text>
            </Box>
          </HStack>
          <HStack spacing={2} justify="flex-end">
            {onBackToLock && (
              <Button
                variant="outline"
                borderColor="gray.300"
                onClick={onBackToLock}
                h="40px"
                borderRadius="lg"
              >
                Back to alert
              </Button>
            )}
            <Button
              bg={theme.accent}
              color="white"
              _hover={{ bg: theme.accent, opacity: 0.9 }}
              onClick={onExtendEta}
              isLoading={isExtendLoading}
              h="40px"
              borderRadius="lg"
              minW="140px"
            >
              Extend ETA
            </Button>
          </HStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={theme.showDismiss ? onDismiss ?? (() => undefined) : () => undefined}
      isCentered
      closeOnEsc={theme.showDismiss}
      closeOnOverlayClick={theme.closeOnOverlay}
      size="md"
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        aria-labelledby={ETA_ALERT_TITLE_ID}
        borderRadius="2xl"
        mx={4}
        overflow="visible"
        maxW="420px"
        bg="transparent"
        boxShadow="none"
      >
        <VisuallyHidden>
          <ModalHeader id={ETA_ALERT_TITLE_ID}>{theme.title}</ModalHeader>
        </VisuallyHidden>
        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="2xl"
          position="relative"
          overflow="hidden"
        >
          <ModalBody px={6} pt={6} pb={5}>
            {theme.showDismiss && (
              <Box position="absolute" top={3} right={3} zIndex={1}>
                <Button
                  variant="ghost"
                  size="sm"
                  minW="auto"
                  h="auto"
                  p={1}
                  aria-label="Dismiss"
                  onClick={onDismiss}
                >
                  <Icon as={FiX} boxSize={5} color="gray.500" />
                </Button>
              </Box>
            )}

            <VStack spacing={4} align="stretch" textAlign="center">
              <Flex justify="center">
                <Flex
                  align="center"
                  justify="center"
                  w="56px"
                  h="56px"
                  borderRadius="full"
                  bg={theme.accent}
                  color="white"
                >
                  <Text fontSize="2xl" fontWeight="bold" lineHeight="1">
                    !
                  </Text>
                </Flex>
              </Flex>

              <Text
                aria-hidden="true"
                fontSize="xl"
                fontWeight="bold"
                color={theme.titleColor}
                lineHeight="1.3"
              >
                {theme.title}
              </Text>

              <Text fontSize="md" color="gray.700">
                {theme.body(orderNo)}
              </Text>

              <HStack
                spacing={3}
                align="center"
                bg={theme.accentSoft}
                borderRadius="xl"
                px={4}
                py={3}
                textAlign="left"
              >
                <Flex
                  align="center"
                  justify="center"
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg="white"
                  flexShrink={0}
                >
                  <Icon as={FiCalendar} boxSize={5} color={theme.accent} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                    Estimated Date & Time
                  </Text>
                  <Text fontSize="md" fontWeight="bold" color={theme.accent}>
                    {formatEtaDisplay(eta)}
                  </Text>
                </Box>
              </HStack>

              {alertType === "hard" ? (
                <HStack spacing={2} justify="center" color={theme.accent}>
                  <Icon as={FiAlertTriangle} boxSize={4} flexShrink={0} />
                  <Text fontSize="sm" fontWeight="medium" textAlign="left">
                    {theme.hint}
                  </Text>
                </HStack>
              ) : (
                <Text fontSize="sm" color="gray.600">
                  {theme.hint}
                </Text>
              )}

              {canViewDetails && (
                <Button
                  variant="outline"
                  borderColor="gray.300"
                  color="gray.800"
                  leftIcon={<Icon as={FiExternalLink} />}
                  onClick={onViewDetails}
                  h="44px"
                  borderRadius="lg"
                  w="100%"
                >
                  View enquiry / order details
                </Button>
              )}

              <HStack spacing={3} pt={1} justify="stretch">
                {theme.showDismiss && (
                  <Button
                    flex={1}
                    variant="outline"
                    borderColor="gray.300"
                    color="gray.800"
                    onClick={onDismiss}
                    h="44px"
                    borderRadius="lg"
                  >
                    Dismiss
                  </Button>
                )}
                <Button
                  flex={1}
                  bg={theme.accent}
                  color="white"
                  _hover={{ bg: theme.accent, opacity: 0.9 }}
                  _active={{ bg: theme.accent, opacity: 0.85 }}
                  onClick={onExtendEta}
                  isLoading={isExtendLoading}
                  h="44px"
                  borderRadius="lg"
                >
                  Extend ETA
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </Box>

        <Box
          mt={4}
          color="white"
          fontSize="sm"
          fontWeight="medium"
          textAlign="center"
          px={2}
        >
          {alertType === "hard" ? (
            <HStack spacing={2} justify="center">
              <Icon as={FiLock} boxSize={4} />
              <Text as="span">{theme.footer}</Text>
            </HStack>
          ) : (
            theme.footer
          )}
        </Box>
      </ModalContent>
    </Modal>
  );
};

export default EtaAlertModal;
